/**
 * DemandList - Sprint 7 Demand Management
 * Table view of demands with filters and actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Plus, Filter, RefreshCw, Search, ArrowUpDown,
  Eye, Edit, Trash2, ChevronLeft, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  intake: 'bg-blue-100 text-blue-800',
  assessment: 'bg-yellow-100 text-yellow-800',
  governance: 'bg-purple-100 text-purple-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800'
};

const IMPACT_COLORS = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-yellow-100 text-yellow-700',
  high: 'bg-red-100 text-red-700'
};

const DemandList = () => {
  const navigate = useNavigate();
  const [demands, setDemands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    department: '',
    category: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });
  const [sortField, setSortField] = useState('-created_at');

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    // TENANT ISOLATION: Include X-Tenant-ID header for sysadmin impersonation
    const impersonatedTenantId = localStorage.getItem('impersonated_tenant_id');
    const userTenantId = localStorage.getItem('simpliview_user_tenant_id');
    const activeTenantId = impersonatedTenantId || userTenantId;
    
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    
    if (activeTenantId) {
      headers['X-Tenant-ID'] = activeTenantId;
    }
    
    return headers;
  }, []);

  const fetchDemands = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('page_size', pagination.pageSize);
      params.append('sort', sortField);
      
      if (filters.status) params.append('status', filters.status);
      if (filters.department) params.append('department', filters.department);
      if (filters.category) params.append('category', filters.category);

      const response = await fetch(`${API_URL}/api/v2/demands?${params}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setDemands(data.data || []);
        setPagination(prev => ({
          ...prev,
          total: data.meta?.total || 0,
          totalPages: data.meta?.total_pages || 0
        }));
      }
    } catch (err) {
      console.error('Failed to fetch demands:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, pagination.page, pagination.pageSize, sortField, filters]);

  useEffect(() => {
    fetchDemands();
  }, [fetchDemands]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortField(`-${field}`);
    } else if (sortField === `-${field}`) {
      setSortField(field);
    } else {
      setSortField(field);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this demand?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/v2/demands/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        fetchDemands();
      }
    } catch (err) {
      console.error('Failed to delete demand:', err);
    }
  };

  const filteredDemands = demands.filter(d => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const title = d.attributes?.title?.toLowerCase() || '';
      const dept = d.attributes?.department?.toLowerCase() || '';
      if (!title.includes(search) && !dept.includes(search)) {
        return false;
      }
    }
    return true;
  });

  return (
    <div className="p-6 space-y-4" data-testid="demand-list">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Demand Management</h1>
          <p className="text-gray-500">Track and manage business demands through the governance pipeline</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/demands/board')} data-testid="view-board-btn">
            View Board
          </Button>
          <Button onClick={() => navigate('/demands/new')} data-testid="create-demand-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Demand
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">Filters:</span>
            </div>

            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="px-3 py-1.5 text-sm border rounded-md bg-white"
              data-testid="filter-status"
            >
              <option value="">All Statuses</option>
              <option value="intake">Intake</option>
              <option value="assessment">Assessment</option>
              <option value="governance">Governance</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search title or department..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="px-3 py-1.5 text-sm border rounded-md w-64"
                data-testid="search-input"
              />
            </div>

            <Button variant="ghost" size="sm" onClick={fetchDemands}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="demands-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th 
                    className="text-left py-3 px-4 font-medium cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('title')}
                  >
                    <div className="flex items-center gap-1">
                      Title
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </th>
                  <th className="text-left py-3 px-4 font-medium">Department</th>
                  <th className="text-left py-3 px-4 font-medium">Category</th>
                  <th className="text-left py-3 px-4 font-medium">Status</th>
                  <th className="text-left py-3 px-4 font-medium">Impact</th>
                  <th 
                    className="text-left py-3 px-4 font-medium cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('score')}
                  >
                    <div className="flex items-center gap-1">
                      Score
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </th>
                  <th 
                    className="text-left py-3 px-4 font-medium cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-1">
                      Created
                      <ArrowUpDown className="h-3 w-3" />
                    </div>
                  </th>
                  <th className="text-right py-3 px-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="py-8 text-center text-gray-500">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                      Loading demands...
                    </td>
                  </tr>
                ) : filteredDemands.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="py-8 text-center text-gray-500">
                      No demands found
                    </td>
                  </tr>
                ) : (
                  filteredDemands.map((demand) => (
                    <tr 
                      key={demand.id} 
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/demands/${demand.id}`)}
                      data-testid={`demand-row-${demand.id}`}
                    >
                      <td className="py-3 px-4 font-medium">
                        {demand.attributes?.title}
                        {demand.attributes?.opex_flag && (
                          <Badge className="ml-2 bg-orange-100 text-orange-700 text-xs">OPEX</Badge>
                        )}
                      </td>
                      <td className="py-3 px-4">{demand.attributes?.department}</td>
                      <td className="py-3 px-4">{demand.attributes?.category || '-'}</td>
                      <td className="py-3 px-4">
                        <Badge className={STATUS_COLORS[demand.attributes?.status] || 'bg-gray-100'}>
                          {demand.attributes?.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={IMPACT_COLORS[demand.attributes?.impact] || 'bg-gray-100'}>
                          {demand.attributes?.impact}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`font-medium ${
                          demand.attributes?.score >= 7 ? 'text-green-600' :
                          demand.attributes?.score >= 4 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {demand.attributes?.score?.toFixed(1)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-500">
                        {demand.attributes?.created_at 
                          ? new Date(demand.attributes.created_at).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/demands/${demand.id}`)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/demands/${demand.id}/edit`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDelete(demand.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-gray-500">
                Showing {((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
                {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
                {pagination.total} demands
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(p => ({...p, page: p.page - 1}))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 py-1 text-sm">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPagination(p => ({...p, page: p.page + 1}))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DemandList;
